<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '../stores/auth';
import { useCartStore } from '../stores/cart';
import { useFavoritesStore } from '../stores/favorites';
import OgKeysLogo from './OgKeysLogo.vue';
// Language switcher hidden while only English is offered (see i18n/index.ts).
// import LanguageSwitcher from './LanguageSwitcher.vue';

const auth = useAuthStore();
const cart = useCartStore();
const favorites = useFavoritesStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);
const mobileOpen = ref(false);

const userInitials = computed(() => {
  const f = auth.user?.firstName?.trim();
  const l = auth.user?.lastName?.trim();
  if (f || l) {
    return [(f?.[0] ?? ''), (l?.[0] ?? '')].join('').toUpperCase() || 'U';
  }
  const username = auth.user?.username?.trim();
  if (username) return username.slice(0, 2).toUpperCase();
  const email = auth.user?.email ?? '';
  return email.slice(0, 2).toUpperCase() || 'U';
});

const displayName = computed(() => {
  const full = [auth.user?.firstName, auth.user?.lastName].filter(Boolean).join(' ');
  return full || auth.user?.username || auth.user?.email?.split('@')[0] || t('common.yourAccount');
});

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
  if (menuOpen.value) {
    queueMicrotask(() => document.addEventListener('click', onOutsideClick));
  }
}

function closeMenu() {
  menuOpen.value = false;
  document.removeEventListener('click', onOutsideClick);
}

function onOutsideClick(e: MouseEvent) {
  if (menuRef.value && !menuRef.value.contains(e.target as Node)) {
    closeMenu();
  }
}

onBeforeUnmount(() => document.removeEventListener('click', onOutsideClick));

function toggleMobile() {
  mobileOpen.value = !mobileOpen.value;
}

function closeMobile() {
  mobileOpen.value = false;
}

// Close the mobile drawer whenever the route changes (e.g. after tapping a link).
watch(() => route.fullPath, closeMobile);

async function logout() {
  closeMenu();
  closeMobile();
  await auth.logout();
  router.push('/');
}
</script>

<template>
  <header class="bg-[#fdf8f8] border-b border-black/10 sticky top-0 z-30">
    <div class="relative max-w-[1280px] mx-auto h-20 flex items-center justify-between px-6 md:px-20">
      <button
        type="button"
        class="md:hidden grid place-items-center size-10 -ml-2 text-black hover:opacity-70"
        :aria-expanded="mobileOpen"
        aria-controls="mobile-menu"
        aria-label="Toggle navigation menu"
        @click.stop="toggleMobile"
      >
        <svg v-if="!mobileOpen" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M6 6 18 18M18 6 6 18" />
        </svg>
      </button>

      <RouterLink
        to="/"
        class="text-black absolute left-1/2 -translate-x-1/2 md:static md:left-auto md:translate-x-0"
        aria-label="GladKey — home"
        @click="closeMobile"
      >
        <OgKeysLogo class="h-7 w-auto" />
      </RouterLink>

      <nav class="hidden md:flex items-center gap-6 text-[16px] text-[#444748]">
        <RouterLink to="/catalog" class="hover:text-black transition-colors">{{ $t('nav.catalog') }}</RouterLink>
        <RouterLink to="/articles" class="hover:text-black transition-colors">{{ $t('nav.articles') }}</RouterLink>
        <RouterLink to="/help" class="hover:text-black transition-colors">{{ $t('nav.faq') }}</RouterLink>
      </nav>

      <div class="hidden md:flex items-center gap-6">
        <!-- <LanguageSwitcher variant="light" /> -->
        <div class="hidden sm:flex items-center gap-4 text-[#1c1b1b]">
          <RouterLink to="/catalog" :aria-label="$t('common.searchCatalog')" class="size-[18px] hover:opacity-70">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="7" />
              <path stroke-linecap="round" d="m20 20-3.5-3.5" />
            </svg>
          </RouterLink>
          <RouterLink
            v-if="auth.isAuthenticated"
            to="/favorites"
            :aria-label="$t('common.favorites')"
            class="relative size-[20px] hover:opacity-70"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </RouterLink>
          <RouterLink v-if="auth.isAuthenticated" to="/cart" :aria-label="$t('common.cart')" class="relative size-[18px] hover:opacity-70">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l2 13h12l2-9H6" />
              <circle cx="9" cy="20" r="1.5" />
              <circle cx="18" cy="20" r="1.5" />
            </svg>
            <span
              v-if="cart.count > 0"
              class="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[16px] h-[16px] rounded-full bg-black text-white text-[10px] font-semibold px-1"
            >{{ cart.count }}</span>
          </RouterLink>
        </div>

        <template v-if="auth.isAuthenticated">
          <div ref="menuRef" class="relative">
            <button
              type="button"
              :aria-expanded="menuOpen"
              :aria-label="displayName + ' menu'"
              class="grid place-items-center size-8 rounded-full bg-[#0a0a0a] text-white font-bold text-[12px] hover:opacity-90 transition-opacity"
              @click.stop="toggleMenu"
            >
              {{ userInitials }}
            </button>

            <div
              v-if="menuOpen"
              role="menu"
              class="absolute right-0 top-[calc(100%+12px)] w-[220px] bg-white rounded-[14px] shadow-[0_8px_24px_0_rgba(0,0,0,0.12)] p-2 z-40"
            >
              <div class="flex flex-col gap-[2px] px-3 pt-2 pb-3">
                <p class="font-bold text-[14px] text-[#0a0a0a] truncate">{{ displayName }}</p>
                <p class="text-[12px] text-[#5c574a] truncate">{{ auth.user?.email }}</p>
              </div>
              <div aria-hidden="true" class="h-px bg-[rgba(10,10,10,0.08)] mx-1"></div>
              <ul class="flex flex-col pt-1">
                <li>
                  <RouterLink
                    to="/account"
                    role="menuitem"
                    class="flex items-center h-9 px-3 rounded-[8px] font-medium text-[14px] text-[#0a0a0a] hover:bg-[rgba(10,10,10,0.04)]"
                    @click="closeMenu"
                  >{{ $t('common.accountDashboard') }}</RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/orders"
                    role="menuitem"
                    class="flex items-center h-9 px-3 rounded-[8px] font-medium text-[14px] text-[#0a0a0a] hover:bg-[rgba(10,10,10,0.04)]"
                    @click="closeMenu"
                  >{{ $t('common.orders') }}</RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/account/billing"
                    role="menuitem"
                    class="flex items-center h-9 px-3 rounded-[8px] font-medium text-[14px] text-[#0a0a0a] hover:bg-[rgba(10,10,10,0.04)]"
                    @click="closeMenu"
                  >{{ $t('common.billingWallet') }}</RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/account/settings"
                    role="menuitem"
                    class="flex items-center h-9 px-3 rounded-[8px] font-medium text-[14px] text-[#0a0a0a] hover:bg-[rgba(10,10,10,0.04)]"
                    @click="closeMenu"
                  >{{ $t('common.settings') }}</RouterLink>
                </li>
              </ul>
              <div aria-hidden="true" class="h-px bg-[rgba(10,10,10,0.08)] mx-1 mt-1"></div>
              <button
                type="button"
                role="menuitem"
                class="w-full flex items-center h-9 px-3 mt-1 rounded-[8px] font-medium text-[14px] text-[#b53b2b] hover:bg-[rgba(181,59,43,0.06)]"
                @click="logout"
              >{{ $t('common.signOut') }}</button>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="flex items-center gap-4">
            <RouterLink to="/login" class="font-mono text-[14px] text-[#1c1b1b] hover:opacity-70">{{ $t('common.logIn') }}</RouterLink>
            <RouterLink
              to="/register"
              class="font-mono text-[14px] text-white bg-black rounded-full px-6 py-2 hover:opacity-90 transition-opacity"
            >{{ $t('common.signUp') }}</RouterLink>
          </div>
        </template>
      </div>
    </div>

    <!-- Mobile navigation drawer (md:hidden) -->
    <div
      v-if="mobileOpen"
      id="mobile-menu"
      class="md:hidden border-t border-black/10 bg-[#fdf8f8] max-h-[calc(100vh-5rem)] overflow-y-auto"
    >
      <nav class="px-6 py-2 flex flex-col">
        <RouterLink to="/catalog" class="py-3 text-[18px] text-[#1c1b1b] hover:text-black border-b border-black/5" @click="closeMobile">{{ $t('nav.catalog') }}</RouterLink>
        <RouterLink to="/articles" class="py-3 text-[18px] text-[#1c1b1b] hover:text-black border-b border-black/5" @click="closeMobile">{{ $t('nav.articles') }}</RouterLink>
        <RouterLink to="/help" class="py-3 text-[18px] text-[#1c1b1b] hover:text-black border-b border-black/5" @click="closeMobile">{{ $t('nav.faq') }}</RouterLink>

        <template v-if="auth.isAuthenticated">
          <RouterLink to="/favorites" class="py-3 text-[18px] text-[#1c1b1b] hover:text-black border-b border-black/5" @click="closeMobile">{{ $t('common.favorites') }}<span v-if="favorites.count > 0"> ({{ favorites.count }})</span></RouterLink>
          <RouterLink to="/cart" class="py-3 text-[18px] text-[#1c1b1b] hover:text-black border-b border-black/5" @click="closeMobile">{{ $t('common.cart') }}<span v-if="cart.count > 0"> ({{ cart.count }})</span></RouterLink>
          <RouterLink to="/account" class="py-3 text-[18px] text-[#1c1b1b] hover:text-black border-b border-black/5" @click="closeMobile">{{ $t('common.accountDashboard') }}</RouterLink>
          <RouterLink to="/orders" class="py-3 text-[18px] text-[#1c1b1b] hover:text-black border-b border-black/5" @click="closeMobile">{{ $t('common.orders') }}</RouterLink>
          <RouterLink to="/account/billing" class="py-3 text-[18px] text-[#1c1b1b] hover:text-black border-b border-black/5" @click="closeMobile">{{ $t('common.billingWallet') }}</RouterLink>
          <RouterLink to="/account/settings" class="py-3 text-[18px] text-[#1c1b1b] hover:text-black border-b border-black/5" @click="closeMobile">{{ $t('common.settings') }}</RouterLink>
          <button type="button" class="py-3 text-left text-[18px] text-[#b53b2b] hover:opacity-80" @click="logout">{{ $t('common.signOut') }}</button>
        </template>
        <template v-else>
          <div class="flex items-center gap-3 py-4">
            <RouterLink to="/login" class="flex-1 text-center py-3 rounded-full border border-black/20 font-mono text-[14px] text-[#1c1b1b] hover:bg-black/[0.04]" @click="closeMobile">{{ $t('common.logIn') }}</RouterLink>
            <RouterLink to="/register" class="flex-1 text-center py-3 rounded-full bg-black text-white font-mono text-[14px] hover:opacity-90" @click="closeMobile">{{ $t('common.signUp') }}</RouterLink>
          </div>
        </template>

        <!-- <div class="py-4 mt-1 border-t border-black/5">
          <LanguageSwitcher variant="light" />
        </div> -->
      </nav>
    </div>
  </header>
</template>
